import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Button } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { keyframes } from "@emotion/react";
import SnackBar from "../../SnackBar";
import ModalEditarVersao from "../ModalEditarVersao";
import styles from "./styles.module.css";
import { buscarPreviaVersaoPisCofins } from "../../../services/api-piscofins.service";
import {
  buscarPreviaVersaoIcms,
  executarMotorAutomatico,
  executarMotorAutomaticoIcms,
  excluirERecriarVersaoPreviaCompleta,
} from "../../../services/api-planejamento-tributario.service";
import StepsGeneric from "../../StepsGeneric";
import Loading from "../../Loading";
import ResumoStepTable from "./ResumoStepTable";
import GerarRelatorioResumo from "../../TableCardResumo/GerarRelatorioResumo";
import SnackBarProgresso from "../../SnackBarProgresso";
import useClientPolling from "../../../hooks/useClientPolling";

// Animação de pulse (padrão do projeto)
const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

const MainComponente = ({ closeModal, tipoPrevia, contextHook }) => {
  const [loadingVersao, setLoadingVersao] = useState(false);
  const [loadedVersao, setLoadedVersao] = useState(false);
  const [executandoMotor, setExecutandoMotor] = useState(false);
  const [loadingProximo, setLoadingProximo] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [showRelatorio, setShowRelatorio] = useState(false);
  // Estado estruturado para dados do relatório
  const [dadosRelatorioEstruturado, setDadosRelatorioEstruturado] = useState(
    {}
  );
  // Estados para modal de edição
  const [openEditModal, setOpenEditModal] = useState(false);

  const {
    versaoSelecionada,
    dadosVersao: dadosContexto,
    setDadosVersao,
  } = contextHook();

  // Cache para evitar reprocessamento desnecessário
  const cacheRef = useRef(new Map());

  // ✅ HOOK Client Polling para execução de motores
  const {
    progresso,
    erro,
    resultado,
    loading: sseLoading,
    executarMotor,
    limparEstado,
  } = useClientPolling();

  // ✅ Limpar estado ao montar e desmontar o componente (evita mostrar erros/resultados anteriores)
  useEffect(() => {
    limparEstado();
    
    return () => {
      // Cleanup ao desmontar
      limparEstado();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ Função para mapear etapas para números (atualizado com etapas do backend)
  const mapearEtapa = useCallback((etapa) => {
    const etapas = {
      validacao: 1,
      busca_grupo: 2,
      busca_empresas: 3,
      validacao_schemas: 4,
      criacao: 5,
      execucao_motores: 6,
      consolidacao: 7,
      concluido: 8,
      erro: 0,
    };
    return etapas[etapa] || 1;
  }, []);

  // Função dinâmica para buscar versão baseada no tipoPrevia
  const buscarVersaoPrevia = useCallback(
    async (idVersao) => {
      if (tipoPrevia === "PIS/COFINS") {
        return await buscarPreviaVersaoPisCofins(idVersao);
      } else if (tipoPrevia === "ICMS") {
        return await buscarPreviaVersaoIcms(idVersao);
      }
      throw new Error(`Tipo de prévia não suportado: ${tipoPrevia}`);
    },
    [tipoPrevia]
  );

  // Função dinâmica para executar motor baseada no tipoPrevia
  const executarMotorPrevia = useCallback(
    async (idPreviaVersao) => {
      if (tipoPrevia === "PIS/COFINS") {
        return await executarMotorAutomatico(idPreviaVersao);
      } else if (tipoPrevia === "ICMS") {
        return await executarMotorAutomaticoIcms(idPreviaVersao);
      }
      throw new Error(`Tipo de prévia não suportado para motor: ${tipoPrevia}`);
    },
    [tipoPrevia]
  );

  // Função otimizada para processar dados iniciais
  const processarDadosIniciais = useCallback((resumos) => {
    if (!resumos || resumos.length === 0) return {};

    // Verificar cache primeiro
    const cacheKey = JSON.stringify(resumos.map((r) => r.idPreviaMotorResumo));
    if (cacheRef.current.has(cacheKey)) {
      return cacheRef.current.get(cacheKey);
    }

    const dadosEstruturados = {};
    const CHUNK_SIZE = 1000; // Processar 1000 linhas por vez

    resumos.forEach((resumo, index) => {
      const descricao =
        resumo?.resumo?.complemento?.descricao || `Step${index + 1}`;
      const chave = descricao.replace(/\s+/g, "");
      const dados = resumo?.resumo?.dados || [];

      if (dados.length === 0) {
        dadosEstruturados[chave] = [];
        return;
      }

      // Para grandes volumes, processar em chunks
      if (dados.length > CHUNK_SIZE) {
        const valoresCalculados = [];

        for (let i = 0; i < dados.length; i += CHUNK_SIZE) {
          const chunk = dados.slice(i, i + CHUNK_SIZE);
          const valoresChunk = chunk.map((row, rowIndex) => {
            const composicaoProjeto = row["Composição Projeto__boolean"];
            const composicaoCalculo = row["Composição Calculo__monetary"];

            if (composicaoProjeto === true && composicaoCalculo) {
              return {
                index: i + rowIndex,
                valor: Number(composicaoCalculo) || 0,
              };
            }
            return null;
          });

          valoresCalculados.push(...valoresChunk.filter(Boolean));
        }

        dadosEstruturados[chave] = valoresCalculados;
      } else {
        // Processamento normal para volumes menores
        const valoresCalculados = dados.map((row, rowIndex) => {
          const composicaoProjeto = row["Composição Projeto__boolean"];
          const composicaoCalculo = row["Composição Calculo__monetary"];

          if (composicaoProjeto === true && composicaoCalculo) {
            return {
              index: rowIndex,
              valor: Number(composicaoCalculo) || 0,
            };
          }
          return null;
        });

        dadosEstruturados[chave] = valoresCalculados.filter(Boolean);
      }
    });

    // Salvar no cache
    cacheRef.current.set(cacheKey, dadosEstruturados);
    return dadosEstruturados;
  }, []);

  // Processar dados estruturados quando resumos mudarem
  useEffect(() => {
    if (dadosContexto?.resumos && dadosContexto.resumos.length > 0) {
      const dadosEstruturados = processarDadosIniciais(dadosContexto.resumos);
      setDadosRelatorioEstruturado(dadosEstruturados);
    }
  }, [dadosContexto?.resumos, processarDadosIniciais]);

  useEffect(() => {
    const idPreviaVersao = versaoSelecionada?.idPreviaVersao;
    if (!idPreviaVersao) return;

    let cancelado = false;

    async function carregar() {
      setLoadingVersao(true);
      try {
        const data = await buscarVersaoPrevia(idPreviaVersao);

        if (cancelado) return;

        setDadosVersao(data);
      } catch (e) {
        console.error("Erro ao carregar dados:", e);
        if (cancelado) return;
        setObjSnackBar({ type: "error", msg: "Erro ao carregar versão." });
      } finally {
        if (!cancelado) {
          setLoadingVersao(false);
          setLoadedVersao(true);
        }
      }
    }
    carregar();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versaoSelecionada, tipoPrevia]); // buscarVersaoPrevia e setDadosVersao são funções estáveis

  // Executa motor automaticamente quando não houver dados de resumo
  const semDadosShownRef = useRef(false);
  useEffect(() => {
    if (!loadedVersao || semDadosShownRef.current) return;
    const qtd = Array.isArray(dadosContexto?.resumos)
      ? dadosContexto.resumos.length
      : 0;
    if (qtd === 0) {
      semDadosShownRef.current = true;
      executarMotorEgerarResumos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedVersao, dadosContexto]); // executarMotorEgerarResumos é função estável

  // ✅ Função global para reabrir modal
  useEffect(() => {
    window.reabrirModalAnalisar = () => {
      setOpenEditModal(false);
    };

    return () => {
      delete window.reabrirModalAnalisar;
    };
  }, []);

  // Função para executar motor automaticamente
  // ✅ MODIFICADA: Função para executar motor com Client Polling
  const executarMotorEgerarResumos = async () => {
    if (!versaoSelecionada?.idPreviaVersao) return;

    try {
      limparEstado();

      await executarMotor({
        idPreviaVersao: versaoSelecionada.idPreviaVersao,
        tipoPrevia: tipoPrevia,
      });
    } catch (error) {
      console.error("❌ Erro ao executar motor:", error);
      setObjSnackBar({
        type: "error",
        msg: "Erro ao executar motor. Tente novamente.",
      });
    }
  };

  // Função para mapear nome do motor baseado no idTipoPrevia
  const obterNomeMotor = useCallback(
    (idTipoPrevia) => {
      if (!dadosContexto?.previa?.categoria?.tiposPrevia)
        return `Motor ${idTipoPrevia}`;

      const tipoPrevia = dadosContexto.previa.categoria.tiposPrevia.find(
        (tp) => tp.idTipoPrevia === idTipoPrevia
      );

      return tipoPrevia?.descricao || `Motor ${idTipoPrevia}`;
    },
    [dadosContexto]
  );

  const { stepsLabels, stepsComponents, hasResumos } = useMemo(() => {
    const resumos = dadosContexto?.resumos || [];
    const hasResumos = resumos.length > 0;

    const labels = [];
    const comps = [];

    if (loadingVersao) {
      // Durante o carregamento, mostra loading
      labels.push("Processando...");
      comps.push(() => (
        <div
          style={{
            textAlign: "center",
            padding: "3rem 2rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px",
          }}
        >
          <Loading loading={true} />
          {semDadosShownRef.current && (
            <p
              style={{
                marginTop: "1.5rem",
                color: "#666",
                fontSize: "1.1rem",
                fontWeight: "500",
              }}
            >
              Gerando resumos automaticamente...
            </p>
          )}
        </div>
      ));
    } else if (hasResumos) {
      // Monta os steps de Resumo existentes
      for (let idx = 0; idx < resumos.length; idx++) {
        const r = resumos[idx];
        // Usar nome do motor baseado no idTipoPrevia
        const nomeMotor = obterNomeMotor(r.idTipoPrevia);
        const desc = `${nomeMotor}`;
        labels.push(desc);

        comps.push(() => (
          <ResumoStepTable
            resumo={{
              dados: r?.resumo?.dados || r?.dados || [],
              colunas: r?.resumo?.colunas || r?.colunas || {},
              cnpj: dadosContexto?.previa?.cnpj,
              descricao: desc,
            }}
            idPreviaVersao={
              dadosContexto?.versao?.idPreviaVersao || r?.idPreviaVersao
            }
            idPreviaMotorResumo={r?.idPreviaMotorResumo}
            indiceResumo={idx}
            loading={false}
            tipoPrevia={tipoPrevia}
            onComposicaoMudou={handleComposicaoMudou}
          />
        ));
      }
    } else {
      // Quando não há resumos e carregamento terminou, mostra mensagem
      labels.push("Aguardando execução");
      comps.push(() => (
        <div
          style={{
            padding: "3rem 2rem",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px",
          }}
        >
          <Inventory2OutlinedIcon
            sx={{
              fontSize: "120px",
              color: "#18316e",
              marginBottom: "2rem",
              animation: `${pulse} 1.5s infinite ease-in-out`,
            }}
          />
          <p style={{ 
            marginBottom: "1rem", 
            fontSize: "1.3rem", 
            fontWeight: "600",
            color: "#18316e"
          }}>
            Nenhum resumo encontrado
          </p>
          <p style={{ 
            color: "#666", 
            fontSize: "1rem",
            maxWidth: "500px",
            lineHeight: "1.6"
          }}>
            {semDadosShownRef.current 
              ? "O motor de cálculo está sendo executado automaticamente. Aguarde alguns instantes..."
              : "Clique em 'Processar Resumos' para executar o motor de cálculo e gerar os dados."}
          </p>
        </div>
      ));
    }

    return { stepsLabels: labels, stepsComponents: comps, hasResumos };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dadosContexto, loadingVersao, obterNomeMotor]); // handleComposicaoMudou e tipoPrevia não mudam

  const [activeStep, setActiveStep] = useState(0);

  const handleGerarPrevia = async () => {
    if (!versaoSelecionada?.idPreviaVersao) {
      return;
    }

    setExecutandoMotor(true);
    try {
      // Executar motor automaticamente para garantir dados atualizados
      await executarMotorPrevia(versaoSelecionada.idPreviaVersao);

      // Recarregar dados após execução do motor
      const novosDados = await buscarVersaoPrevia(
        versaoSelecionada.idPreviaVersao
      );

      if (novosDados) {
        setDadosVersao(novosDados);
        setObjSnackBar({
          type: "success",
          msg: "Dados atualizados! Abrindo relatório...",
        });
      }

      // Abrir relatório com dados atualizados
      setShowRelatorio(true);
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg: "Erro ao atualizar dados. Abrindo relatório com dados atuais...",
      });

      // Mesmo com erro, abrir relatório com dados existentes
      setShowRelatorio(true);
    } finally {
      setExecutandoMotor(false);
    }
  };

  const handleCloseRelatorio = () => {
    setShowRelatorio(false);
  };

  const handleStep = (next) => {
    if (next < 0) return closeModal();
    if (next >= stepsLabels.length) return; // trava

    // Ativar loading apenas para botão "Próximo"
    if (next > activeStep) {
      setLoadingProximo(true);
      // Simular delay para mostrar o loading
      setTimeout(() => {
        setActiveStep(next);
        setLoadingProximo(false);
      }, 800);
    } else {
      setActiveStep(next);
    }
  };

  const handleComposicaoMudou = useCallback((payload) => {
    // Atualizar dados estruturados quando composição mudar
    setDadosRelatorioEstruturado((prev) => ({
      ...prev,
      [payload.chave]: payload.dados,
    }));
  }, []);

  const handleCreateEditVersion = async (nomeVersao, categoria) => {
    if (!versaoSelecionada?.idPreviaVersao) return;

    try {
      setLoadingVersao(true);
      setObjSnackBar({
        type: "info",
        msg: "Atualizando versão...",
      });

      const resultado = await excluirERecriarVersaoPreviaCompleta({
        idPreviaVersao: versaoSelecionada.idPreviaVersao,
        idCategoria: categoria.idCategoriaTributaria,
        usuCadastro: 1,
        nomeVersao: nomeVersao,
        tipoPrevia: tipoPrevia,
      });

      if (resultado.sucesso) {
        setObjSnackBar({
          type: "success",
          msg: resultado.mensagem,
        });

        // Recarregar dados com a nova versão
        const novosDados = await buscarVersaoPrevia(
          resultado.novaVersao.idPreviaVersao
        );
        setDadosVersao(novosDados);
      } else {
        setObjSnackBar({
          type: "error",
          msg: resultado.mensagem,
        });
      }
    } catch (error) {
      setObjSnackBar({
        type: "error",
        msg: "Erro ao atualizar versão. Tente novamente.",
      });
    } finally {
      setLoadingVersao(false);
    }
  };

  const buttonCloseStyle = {
    borderColor: "#18316e",
    color: "#18316e",
    transition: "transform 0.3s ease-in-out",
    "&:hover": { borderColor: "#18316e", transform: "scale(1.1)" },
  };

  const buttonSubmitStyle = {
    backgroundColor: "#18316e",
    transition: "transform 0.3s ease-in-out",
    "&:hover": { backgroundColor: "#18316e", transform: "scale(1.1)" },
  };

  return (
    <div
      className={styles.wrapperModalConteudo}
      style={{ position: "relative" }}
    >
      {loadingProximo && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <Loading loading={true} />
        </div>
      )}
      <div className={styles.stepsWrapper}>
        {stepsLabels.length > 0 && (
          <StepsGeneric
            steps={stepsLabels}
            stepsComponents={stepsComponents}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            data={{}}
            complementaryHeader={null}
          />
        )}
      </div>

      <div className={styles.actionBarSticky}>
        <div className={styles.actionContainer__verbas}>
          <div className={styles.backActions__actionContainer}>
            {activeStep > 0 && (
              <Button
                sx={buttonCloseStyle}
                variant="outlined"
                color="primary"
                type="button"
                onClick={() => handleStep(activeStep - 1)}
                disabled={loadingVersao}
              >
                Voltar
              </Button>
            )}
            {activeStep === 0 && hasResumos && (
              <Button
                sx={buttonCloseStyle}
                variant="outlined"
                color="primary"
                type="button"
                onClick={() => setOpenEditModal(true)}
                disabled={loadingVersao}
              >
                Editar
              </Button>
            )}
            <Button
              sx={buttonCloseStyle}
              variant="outlined"
              color="primary"
              type="button"
              onClick={() => closeModal()}
              disabled={loadingVersao}
            >
              FECHAR
            </Button>
          </div>
          <div>
            {activeStep + 1 < stepsLabels.length ? (
              <Button
                sx={buttonSubmitStyle}
                variant="contained"
                color="primary"
                type="button"
                onClick={() => handleStep(activeStep + 1)}
                disabled={loadingVersao || loadingProximo}
              >
                {loadingProximo ? "Carregando..." : "Próximo"}
              </Button>
            ) : (
              <Button
                sx={buttonSubmitStyle}
                variant="contained"
                color="primary"
                type="button"
                onClick={handleGerarPrevia}
                disabled={!hasResumos || loadingVersao || executandoMotor}
              >
                {executandoMotor ? "Atualizando..." : "Gerar Prévia"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Dialog do Relatório */}
      {showRelatorio && (
        <GerarRelatorioResumo
          resumos={dadosContexto?.resumos || []}
          dadosRelatorioEstruturado={dadosRelatorioEstruturado}
          labels={stepsLabels}
          onClose={handleCloseRelatorio}
          dadosContexto={{
            idPreviaVersao: dadosContexto?.versao?.idPreviaVersao,
            tipoPrevia: dadosContexto?.versao?.tipoPrevia,
            categoria: dadosContexto?.previa?.categoria,
          }}
        />
      )}

      {/* Modal de Editar Versão */}
      {openEditModal && (
        <ModalEditarVersao
          open={openEditModal}
          onClose={() => setOpenEditModal(false)}
          onEdit={handleCreateEditVersion}
          versaoAtual={versaoSelecionada}
          tipoPrevia={tipoPrevia}
          dadosPrevia={{
            razaoSocial: dadosContexto?.previa?.razaoSocial || "Empresa",
          }}
          versoesExistentes={dadosContexto?.resumos || []}
          isProjeto={false} // ModalAnalisar é sempre para prévias
        />
      )}

      {/* ✅ SnackBar de Progresso Client Polling */}
      <SnackBarProgresso
        open={sseLoading || progresso || erro || resultado}
        onClose={() => {
          limparEstado();
        }}
        progressData={(() => {
          const mappedData = {
            titulo: progresso?.mensagem || "Executando motores...",
            progresso: progresso?.progresso || 0,
            etapaAtual: mapearEtapa(progresso?.etapa) || 1,
            totalEtapas: 8,
            descricao: progresso?.descricao || progresso?.mensagem,
            tipoMotor: tipoPrevia,
            tempoEstimado: progresso?.tempoEstimado || "Calculando...",
            sucesso: erro ? false : resultado ? true : undefined,
            erro: erro ? true : false,
          };
          return mappedData;
        })()}
        showLoading={sseLoading && !erro && !resultado}
      />

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </div>
  );
};

export default MainComponente;
